import React from 'react';
import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import PageBaseProps from 'types/PageBase';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import { ErrorTypeEnum, isApiErrorResponse } from 'types/Error';

import { useLoginMFAMutation } from 'modules/auth/service';

import { getConfidentialityPolicyLink, getContext } from 'modules/dealers/selectors';
import { BreakdownServiceAuthorType, SelfServiceType } from 'modules/selfServices/types/SelfService';
import { getLegalNotice, isSelfServiceLoading as isSelfServiceLoadingSelector } from 'modules/selfServices/selectors';
import { canCollectAnalyticsData, getLoginData, getMFAData, getSelectedSelfServiceId } from 'modules/auth/selectors';

import ResendCodeModal from './ResendCodeModal';
import VerifyCodeForm from './VerifyCodeForm';

const OPEN_RESEND_MODAL_EVENT_CONFIG = {
  category: 'Resend',
  action: 'Modal open',
  label: 'Resend modal has been opened',
};

const Verify: React.FC<PageBaseProps & { hideGTC?: boolean }> = ({ onPrev, onNext, hideGTC }) => {
  const intl = useIntl();

  const context = useSelector(getContext);
  const { isDriver } = useSelector(getLoginData);
  const canUseGA = useSelector(canCollectAnalyticsData);
  const confidentialityPolicyLink = useSelector(getConfidentialityPolicyLink);
  const legalNotice = useSelector(getLegalNotice);

  const { selfServicePhoneNumber } = context;

  const {
    type,
    email,
    phoneNumber,
    connectionId,
    remainingAttempt,
    channel = ChannelEnum.SMS,
  } = useSelector(getMFAData);

  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const isSelfServiceLoading = useSelector(isSelfServiceLoadingSelector);

  const [code, setCode] = React.useState('');
  const [displayModal, setDisplayModal] = React.useState(false);

  const [login, { isLoading: isLoginIn, error, isError }] = useLoginMFAMutation();

  const hasWrongCodeError = isApiErrorResponse(error) && error.data.errorType === ErrorTypeEnum.MFA_WRONG_CODE;
  const inputError = hasWrongCodeError
    ? intl.formatMessage({
      id: 'error.types.MFA_WRONG_CODE',
      defaultMessage: 'Wrong code, please try again',
    })
    : isError;

  const shouldDisplayHelper = Boolean(selfServicePhoneNumber?.local) && hasWrongCodeError;

  const isLoading = isLoginIn || isSelfServiceLoading;
  const isDisabled = code.length < 6 || remainingAttempt === 0;

  const breakdownServiceAuthorType = React.useMemo(() => {
    if (type === SelfServiceType.BREAKDOWN) {
      return isDriver ? BreakdownServiceAuthorType.CUSTOMER : BreakdownServiceAuthorType.BREAKDOWN_MECHANIC;
    }
    return undefined;
  }, [isDriver, type]);

  const handleSendCode = React.useCallback(() => {

    if (canUseGA) {
      ReactGA.event(OPEN_RESEND_MODAL_EVENT_CONFIG);
    }

    setDisplayModal(true);
  }, []);

  const handleNextClick = React.useCallback(async () => {
    try {
      await login({
        connectionId,
        selfServiceId,
        code,
        breakdownServiceAuthorType,
      }).unwrap();
      onNext?.();
    } catch {}
  }, [login, connectionId, selfServiceId, code, breakdownServiceAuthorType, onNext]);

  return (
    <>
      <VerifyCodeForm
        code={code}
        onCodeChange={setCode}
        onVerify={handleNextClick}
        onPrev={onPrev}
        onNext={onNext}
        isLoading={isLoading}
        isDisabled={isDisabled}
        inputError={inputError}
        channel={channel}
        contactInfo={channel === ChannelEnum.EMAIL ? email : phoneNumber}
        supportPhoneNumber={selfServicePhoneNumber}
        confidentialityPolicyLink={confidentialityPolicyLink}
        legalNotice={legalNotice}
        hideGTC={hideGTC}
        onResend={handleSendCode}
        shouldDisplayHelper={shouldDisplayHelper}
        canUseGA={canUseGA}
      />
      {displayModal && <ResendCodeModal onClose={setDisplayModal} canUseGA={canUseGA} />}
    </>
  );
};

export default Verify;
